import React from 'react';
import { FiX, FiSave, FiPlus, FiTrash2 } from 'react-icons/fi';

const SectionEditor = ({ section, formData, onSave, onCancel }) => {
  const renderEditor = () => {
    switch (section) {
      case 'summary':
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-medium text-gray-900">Edit Summary</h2>
            <textarea
              className="w-full h-32 p-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              value={formData.summary || ''}
              onChange={(e) => onSave({ ...formData, summary: e.target.value })}
              placeholder="Write a professional summary..."
            />
          </div>
        );
        
      case 'skills':
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-medium text-gray-900">Edit Skills</h2>
            <p className="text-sm text-gray-500">Enter one skill per line</p>
            <textarea
              className="w-full h-32 p-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              value={(formData.skills || []).join('\n')}
              onChange={(e) => {
                const skills = e.target.value
                  .split('\n')
                  .map(skill => skill.trim())
                  .filter(skill => skill !== '');
                onSave({ ...formData, skills });
              }}
              placeholder="JavaScript\nReact\nNode.js\n..."
            />
          </div>
        );
        
      case 'experience':
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-medium text-gray-900">Edit Experience</h2>
            {formData.experience && formData.experience.map((exp, index) => (
              <div key={index} className="border border-gray-200 rounded-md p-4">
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Company</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                      value={exp.company || ''}
                      onChange={(e) => {
                        const updatedExperience = [...formData.experience];
                        updatedExperience[index] = { ...exp, company: e.target.value };
                        onSave({ ...formData, experience: updatedExperience });
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Position</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                      value={exp.position || ''}
                      onChange={(e) => {
                        const updatedExperience = [...formData.experience];
                        updatedExperience[index] = { ...exp, position: e.target.value };
                        onSave({ ...formData, experience: updatedExperience });
                      }}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Start Date</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                      value={exp.start || ''}
                      onChange={(e) => {
                        const updatedExperience = [...formData.experience];
                        updatedExperience[index] = { ...exp, start: e.target.value };
                        onSave({ ...formData, experience: updatedExperience });
                      }}
                      placeholder="YYYY-MM"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">End Date</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                      value={exp.end || ''}
                      onChange={(e) => {
                        const updatedExperience = [...formData.experience];
                        updatedExperience[index] = { ...exp, end: e.target.value };
                        onSave({ ...formData, experience: updatedExperience });
                      }}
                      placeholder="YYYY-MM or leave blank for current"
                    />
                  </div>
                </div>
                
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700">Location</label>
                  <input
                    type="text"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                    value={exp.location || ''}
                    onChange={(e) => {
                      const updatedExperience = [...formData.experience];
                      updatedExperience[index] = { ...exp, location: e.target.value };
                      onSave({ ...formData, experience: updatedExperience });
                    }}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Details (one bullet point per line)
                  </label>
                  <textarea
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 h-32"
                    value={(exp.details || []).join('\n')}
                    onChange={(e) => {
                      const details = e.target.value
                        .split('\n')
                        .map(detail => detail.trim())
                        .filter(detail => detail !== '');
                      
                      const updatedExperience = [...formData.experience];
                      updatedExperience[index] = { ...exp, details };
                      onSave({ ...formData, experience: updatedExperience });
                    }}
                    placeholder="• Accomplished X resulting in Y\n• Led team of Z people"
                  />
                </div>
              </div>
            ))}
          </div>
        );
        
      case 'education':
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-medium text-gray-900">Edit Education</h2>
            {formData.education && formData.education.map((edu, index) => (
              <div key={index} className="border border-gray-200 rounded-md p-4">
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700">School/University</label>
                  <input
                    type="text"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                    value={edu.school || ''}
                    onChange={(e) => {
                      const updatedEducation = [...formData.education];
                      updatedEducation[index] = { ...edu, school: e.target.value };
                      onSave({ ...formData, education: updatedEducation });
                    }}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Degree/Major</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                      value={edu.major || ''}
                      onChange={(e) => {
                        const updatedEducation = [...formData.education];
                        updatedEducation[index] = { ...edu, major: e.target.value };
                        onSave({ ...formData, education: updatedEducation });
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Minor (Optional)</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                      value={edu.minor || ''}
                      onChange={(e) => {
                        const updatedEducation = [...formData.education];
                        updatedEducation[index] = { ...edu, minor: e.target.value };
                        onSave({ ...formData, education: updatedEducation });
                      }}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Start Date</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                      value={edu.start || ''}
                      onChange={(e) => {
                        const updatedEducation = [...formData.education];
                        updatedEducation[index] = { ...edu, start: e.target.value };
                        onSave({ ...formData, education: updatedEducation });
                      }}
                      placeholder="YYYY-MM"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">End Date</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                      value={edu.end || ''}
                      onChange={(e) => {
                        const updatedEducation = [...formData.education];
                        updatedEducation[index] = { ...edu, end: e.target.value };
                        onSave({ ...formData, education: updatedEducation });
                      }}
                      placeholder="YYYY-MM"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">GPA (Optional)</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                      value={edu.gpa || ''}
                      onChange={(e) => {
                        const updatedEducation = [...formData.education];
                        updatedEducation[index] = { ...edu, gpa: e.target.value };
                        onSave({ ...formData, education: updatedEducation });
                      }}
                    />
                  </div>
                </div>
                
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700">Relevant Courses (Optional)</label>
                  <div className="mt-1 space-y-2">
                    {Array.isArray(edu.courses) && edu.courses.map((course, courseIndex) => (
                      <div key={courseIndex} className="flex items-center space-x-2">
                        <input
                          type="text"
                          className="flex-1 border border-gray-300 rounded-md shadow-sm p-2"
                          value={course}
                          onChange={(e) => {
                            const updatedEducation = [...formData.education];
                            const updatedCourses = [...(edu.courses || [])];
                            updatedCourses[courseIndex] = e.target.value;
                            updatedEducation[index] = { ...edu, courses: updatedCourses };
                            onSave({ ...formData, education: updatedEducation });
                          }}
                          placeholder="Course name"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const updatedEducation = [...formData.education];
                            const updatedCourses = [...(edu.courses || [])];
                            updatedCourses.splice(courseIndex, 1);
                            updatedEducation[index] = { ...edu, courses: updatedCourses };
                            onSave({ ...formData, education: updatedEducation });
                          }}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <FiTrash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        const updatedEducation = [...formData.education];
                        const updatedCourses = [...(edu.courses || []), ''];
                        updatedEducation[index] = { ...edu, courses: updatedCourses };
                        onSave({ ...formData, education: updatedEducation });
                      }}
                      className="flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm"
                    >
                      <FiPlus className="h-4 w-4" /> Add Course
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
        
      case 'certificates':
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-medium text-gray-900">Edit Certifications</h2>
            {formData.certificates && formData.certificates.map((cert, index) => (
              <div key={index} className="border border-gray-200 rounded-md p-4">
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700">Certificate Name</label>
                  <input
                    type="text"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                    value={cert.certificate || ''}
                    onChange={(e) => {
                      const updatedCertificates = [...formData.certificates];
                      updatedCertificates[index] = { ...cert, certificate: e.target.value };
                      onSave({ ...formData, certificates: updatedCertificates });
                    }}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Issuing Organization</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                      value={cert.issuer || ''}
                      onChange={(e) => {
                        const updatedCertificates = [...formData.certificates];
                        updatedCertificates[index] = { ...cert, issuer: e.target.value };
                        onSave({ ...formData, certificates: updatedCertificates });
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Date Issued</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                      value={cert.start || ''}
                      onChange={(e) => {
                        const updatedCertificates = [...formData.certificates];
                        updatedCertificates[index] = { ...cert, start: e.target.value };
                        onSave({ ...formData, certificates: updatedCertificates });
                      }}
                      placeholder="YYYY-MM"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
        
      default:
        return <div>Select a section to edit</div>;
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {renderEditor()}
          
          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <FiX className="inline-block mr-1" /> Cancel
            </button>
            <button
              onClick={() => onCancel(true)}
              className="px-4 py-2 bg-primary-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-primary-700"
            >
              <FiSave className="inline-block mr-1" /> Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionEditor;